import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../store";
import {
  getUserRooms,
  createOrGetRoom,
  getRoomHistory,
  sendRoomMessage as sendRoomMessageApi,
  postMessageReaction,
  roomApiToChat,
} from "../../services/chatService";
import {
  Chat,
  ChatMessage,
  ChatState,
  SendMessageRequest,
} from "../../types/chat";
import type { User } from "../../types";
import { ERROR_MESSAGES } from "../../utils/constants";
import {
  mergeUnreadFromPrevious,
  mergeReactionUserIntoReactions,
  applyParticipantDetailsFromHistory,
  mergeHistoryWithRealtime,
  applyCreatedChatToState,
  applyParticipantDetailsToChat,
  isDeactivatedUserSendError,
  isReportBlockSendError,
  resolveSendMessageErrorDisplay,
  markChatOtherUserDeactivated,
} from "../../helpers/chatHelper";

const initialState: ChatState = {
  chats: [],
  currentChat: null,
  messages: [],
  loading: false,
  roomCreationLoading: false,
  error: null,
  unreadCount: 0,
  onlineUserIds: [],
  typingUserIdInRoom: null,
};


export const fetchChatList = createAsyncThunk(
  "chat/fetchChatList",
  async (
    options: { silent?: boolean } | undefined,
    thunkAPI
  ) => {
    const state = thunkAPI.getState() as RootState;
    const userId = state.auth.userId;
    if (!userId) {
      return thunkAPI.rejectWithValue(ERROR_MESSAGES.NOT_LOGGED_IN);
    }
    try {
      const rooms = await getUserRooms(userId);
      const chats = rooms.map((room) => roomApiToChat(room, userId));
      const prev = (thunkAPI.getState() as RootState).chat.chats;
      const merged = mergeUnreadFromPrevious(chats, prev);
      const unreadCount = merged.reduce(
        (sum, c) => sum + (c.unreadCount || 0),
        0
      );
      return { chats: merged, unreadCount };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || ERROR_MESSAGES.FAILED_TO_FETCH_CHATS);
    }
  }
);

export const fetchChatMessages = createAsyncThunk(
  "chat/fetchChatMessages",
  async (roomId: string, thunkAPI) => {
    try {
      const userId = (thunkAPI.getState() as RootState).auth.userId;
      const { messages, participantDetails, otherParticipantDetails } =
        await getRoomHistory(roomId);
      return {
        roomId,
        messages,
        participantDetails,
        otherParticipantDetails,
        userId,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.message || ERROR_MESSAGES.FAILED_TO_FETCH_CHATS
      );
    }
  }
);

/** Same as fetchChatMessages but does not toggle `loading` (e.g. Ably reaction sync). */
/** Open a room by id — list lookup, then room history API for deep links. */
export const openChatByRoomId = createAsyncThunk(
  "chat/openChatByRoomId",
  async (roomId: string, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const userId = state.auth.userId;
    if (!userId) {
      return thunkAPI.rejectWithValue(ERROR_MESSAGES.NOT_LOGGED_IN);
    }

    const trimmedRoomId = roomId.trim();
    if (!trimmedRoomId) {
      return thunkAPI.rejectWithValue(ERROR_MESSAGES.FAILED_TO_FETCH_CHATS);
    }

    let chat = state.chat.chats.find((c) => c.id === trimmedRoomId);
    if (!chat) {
      try {
        const rooms = await getUserRooms(userId);
        const room = rooms.find((r) => r.roomId === trimmedRoomId);
        if (room) {
          chat = roomApiToChat(room, userId);
        }
      } catch (error: any) {
        return thunkAPI.rejectWithValue(
          error.message || ERROR_MESSAGES.FAILED_TO_FETCH_CHATS
        );
      }
    }

    if (!chat) {
      try {
        const { messages, participantDetails, otherParticipantDetails } =
          await getRoomHistory(trimmedRoomId);
        const participantIds = participantDetails.map((p) => p.userId);
        const lastTimestamp =
          messages.length > 0
            ? messages[messages.length - 1].timestamp
            : Date.now();
        const minimalChat: Chat = {
          id: trimmedRoomId,
          roomId: trimmedRoomId,
          participants: participantIds,
          lastActivity: lastTimestamp,
          reactions: {},
          lastMessagePreview:
            messages.length > 0
              ? messages[messages.length - 1].content
              : "",
          lastMessageTime: lastTimestamp,
        };
        chat = applyParticipantDetailsToChat(
          minimalChat,
          participantDetails,
          userId,
          otherParticipantDetails
        );
      } catch (error: any) {
        return thunkAPI.rejectWithValue(
          error.message || ERROR_MESSAGES.FAILED_TO_FETCH_CHATS
        );
      }
    }

    return chat;
  }
);

export const refreshChatMessages = createAsyncThunk(
  "chat/refreshChatMessages",
  async (roomId: string, thunkAPI) => {
    try {
      const userId = (thunkAPI.getState() as RootState).auth.userId;
      const { messages, participantDetails, otherParticipantDetails } =
        await getRoomHistory(roomId);
      return {
        roomId,
        messages,
        participantDetails,
        otherParticipantDetails,
        userId,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.message || ERROR_MESSAGES.FAILED_TO_FETCH_CHATS
      );
    }
  }
);

/** POST /chat/rooms — create or get room for the given participant user IDs */
export const createChatRoom = createAsyncThunk(
  "chat/createChatRoom",
  async (participants: string[], thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const userId = state.auth.userId;
    if (!userId) {
      return thunkAPI.rejectWithValue(ERROR_MESSAGES.NOT_LOGGED_IN);
    }
    if (!participants?.length) {
      return thunkAPI.rejectWithValue(ERROR_MESSAGES.AT_LEAST_ONE_PARTICIPANT_REQUIRED);
    }
    try {
      const room = await createOrGetRoom({ participants });
      const chat = roomApiToChat(room, userId);
      return chat;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.message || ERROR_MESSAGES.FAILED_TO_CREATE_CHAT_ROOM
      );
    }
  }
);

export const createChat = createAsyncThunk(
  "chat/createChat",
  async (receiverId: string, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const userId = state.auth.userId;
    if (!userId) {
      return thunkAPI.rejectWithValue(ERROR_MESSAGES.NOT_LOGGED_IN);
    }
    try {
      const room = await createOrGetRoom({
        participants: [userId, receiverId],
      });
      const chat = roomApiToChat(room, userId);
      return chat;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || ERROR_MESSAGES.FAILED_TO_CREATE_CHAT);
    }
  }
);

export const sendMessage = createAsyncThunk(
  "chat/sendMessage",
  async (data: SendMessageRequest, thunkAPI) => {
    try {
      await sendRoomMessageApi(data);
    } catch (error: unknown) {
      if (isReportBlockSendError(error)) {
        return thunkAPI.rejectWithValue(
          ERROR_MESSAGES.CANNOT_SEND_MESSAGE_REPORTED_USER
        );
      }
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const errorMessage = resolveSendMessageErrorDisplay(
        err.response?.data?.message ||
          err.message ||
          ERROR_MESSAGES.FAILED_TO_SEND_MESSAGE
      );
      if (isDeactivatedUserSendError(error)) {
        thunkAPI.dispatch(markOtherUserDeactivated(data.roomId));
      }
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const addMessageReaction = createAsyncThunk(
  "chat/addMessageReaction",
  async (
    payload: { roomId: string; messageId: string; reaction: string },
    thunkAPI
  ) => {
    const userId = (thunkAPI.getState() as RootState).auth.userId;
    if (!userId) {
      return thunkAPI.rejectWithValue(ERROR_MESSAGES.NOT_LOGGED_IN);
    }
    try {
      await postMessageReaction({
        roomId: payload.roomId,
        messageId: payload.messageId,
        userId,
        reaction: payload.reaction,
      });
      return {
        roomId: payload.roomId,
        messageId: payload.messageId,
        reaction: payload.reaction,
        userId,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.message || ERROR_MESSAGES.FAILED_TO_ADD_REACTION
      );
    }
  }
);

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setCurrentChat(state, action: PayloadAction<Chat | null>) {
      state.currentChat = action.payload;
    },
    clearChatError(state) {
      state.error = null;
    },
    markOtherUserDeactivated(state, action: PayloadAction<string>) {
      markChatOtherUserDeactivated(state, action.payload);
    },
    clearUnreadForRoom(state, action: PayloadAction<string>) {
      const roomId = action.payload;
      const chat = state.chats.find((c) => c.id === roomId);
      if (chat && chat.unreadCount) {
        state.unreadCount = Math.max(
          0,
          state.unreadCount - (chat.unreadCount || 0)
        );
        chat.unreadCount = 0;
      }
    },
    bumpUnreadForRoom(state, action: PayloadAction<string>) {
      const roomId = action.payload;
      if (state.currentChat?.id === roomId) {
        return;
      }
      const chat = state.chats.find((c) => c.id === roomId);
      if (chat) {
        chat.unreadCount = (chat.unreadCount || 0) + 1;
        state.unreadCount += 1;
      } else {
        state.unreadCount += 1;
      }
    },
    addMessage(state, action: PayloadAction<ChatMessage>) {
      const exists = state.messages.some((m) => m.id === action.payload.id);
      if (exists) {
        return;
      }
      state.messages.push(action.payload);
      const chat = state.chats.find((c) => c.id === action.payload.roomId);
      if (chat) {
        chat.lastMessagePreview = action.payload.content;
        chat.lastMessageTime = action.payload.timestamp;
        chat.lastActivity = action.payload.timestamp;
      }
    },
    updateChatLastMessage(
      state,
      action: PayloadAction<{ roomId: string; message: ChatMessage }>
    ) {
      const chat = state.chats.find((c) => c.id === action.payload.roomId);
      if (chat) {
        chat.lastMessagePreview = action.payload.message.content;
        chat.lastMessageTime = action.payload.message.timestamp;
        chat.lastActivity = action.payload.message.timestamp;
        if (chat.id !== state.currentChat?.id) {
          chat.unreadCount = (chat.unreadCount || 0) + 1;
          state.unreadCount += 1;
        }
      }
    },
    patchRoomPreview(
      state,
      action: PayloadAction<{
        roomId: string;
        preview: string;
        time: number;
      }>
    ) {
      const chat = state.chats.find((c) => c.id === action.payload.roomId);
      if (chat) {
        chat.lastMessagePreview = action.payload.preview;
        chat.lastMessageTime = action.payload.time;
        chat.lastActivity = action.payload.time;
      }
    },
    /** `bumpRoomLastActivity` — refresh list ordering when a known room gets a new message alert. */
    bumpRoomLastActivity(state, action: PayloadAction<string>) {
      const roomId = action.payload;
      const chat = state.chats.find((c) => c.id === roomId);
      if (chat) {
        const t = Date.now();
        chat.lastActivity = t;
        chat.lastMessageTime = t;
        state.chats = [...state.chats].sort(
          (a, b) =>
            (b.lastMessageTime ?? b.lastActivity ?? 0) -
            (a.lastMessageTime ?? a.lastActivity ?? 0)
        );
      }
    },
    /** Mobile `applyMessageReactionFromEvent` — merge Ably `message-reaction` without refetch. */
    applyMessageReactionFromEvent(
      state,
      action: PayloadAction<{
        roomId: string;
        messageId: string;
        userId: string;
        reaction: string;
      }>
    ) {
      const { roomId, messageId, userId, reaction } = action.payload;
      if (state.currentChat?.id !== roomId) {
        return;
      }
      const msg = state.messages.find(
        (m) => m.messageId === messageId || m.id === messageId
      );
      if (!msg) {
        return;
      }
      msg.reactions = mergeReactionUserIntoReactions(
        msg.reactions,
        reaction,
        userId
      );
    },
    clearMessages(state) {
      state.messages = [];
      state.currentChat = null;
    },
    incrementUnreadCount(state) {
      state.unreadCount += 1;
    },
    decrementUnreadCount(state, action: PayloadAction<number>) {
      state.unreadCount = Math.max(0, state.unreadCount - action.payload);
    },
    setOnlineUserIds(state, action: PayloadAction<string[]>) {
      state.onlineUserIds = action.payload;
    },
    addOnlineUserId(state, action: PayloadAction<string>) {
      if (!state.onlineUserIds.includes(action.payload)) {
        state.onlineUserIds.push(action.payload);
      }
    },
    removeOnlineUserId(state, action: PayloadAction<string>) {
      state.onlineUserIds = state.onlineUserIds.filter(
        (id) => id !== action.payload
      );
    },
    setTypingUserId(state, action: PayloadAction<string | null>) {
      state.typingUserIdInRoom = action.payload;
    },
    resetChatState() {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChatList.pending, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchChatList.fulfilled, (state, action) => {
        const incoming = action.payload.chats;
        const previous = state.chats;
        const map = new Map(previous.map((c) => [c.id, c]));
        incoming.forEach((c) => {
          const existing = map.get(c.id);
          map.set(c.id, existing ? { ...existing, ...c } : c);
        });
        const combined = Array.from(map.values());
        const merged = mergeUnreadFromPrevious(combined, previous);
        state.chats = merged;
        state.unreadCount = merged.reduce(
          (sum, c) => sum + (c.unreadCount || 0),
          0
        );
        if (state.currentChat?.id) {
          const updatedCurrent = merged.find((chat) => chat.id === state.currentChat?.id);
          if (updatedCurrent) {
            state.currentChat = { ...state.currentChat, ...updatedCurrent };
          }
        }
        state.loading = false;
      })
      .addCase(fetchChatList.rejected, (state, action) => {
        state.loading = false;
        if (!action.meta.arg?.silent) {
          state.error = action.payload as string;
        }
      })
      .addCase(fetchChatMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChatMessages.fulfilled, (state, action) => {
        if (state.currentChat?.id !== action.payload.roomId) {
          state.loading = false;
          return;
        }
        applyParticipantDetailsFromHistory(
          state,
          action.payload.roomId,
          action.payload.participantDetails,
          action.payload.userId,
          action.payload.otherParticipantDetails
        );
        state.messages = mergeHistoryWithRealtime(
          state.messages,
          action.payload.roomId,
          action.payload.messages
        );
        const last = state.messages[state.messages.length - 1];
        if (last) {
          const chat = state.chats.find((c) => c.id === action.payload.roomId);
          if (chat) {
            chat.lastMessagePreview = last.content;
            chat.lastMessageTime = last.timestamp;
          }
        }
        state.loading = false;
      })
      .addCase(fetchChatMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(refreshChatMessages.fulfilled, (state, action) => {
        if (state.currentChat?.id !== action.payload.roomId) {
          return;
        }
        applyParticipantDetailsFromHistory(
          state,
          action.payload.roomId,
          action.payload.participantDetails,
          action.payload.userId,
          action.payload.otherParticipantDetails
        );
        state.messages = mergeHistoryWithRealtime(
          state.messages,
          action.payload.roomId,
          action.payload.messages
        );
        const last = state.messages[state.messages.length - 1];
        if (last) {
          const chat = state.chats.find((c) => c.id === action.payload.roomId);
          if (chat) {
            chat.lastMessagePreview = last.content;
            chat.lastMessageTime = last.timestamp;
          }
        }
      })
      .addCase(addMessageReaction.fulfilled, (state, action) => {
        const { messageId, reaction, userId } = action.payload;
        const msg = state.messages.find(
          (m) => m.messageId === messageId || m.id === messageId
        );
        if (!msg) {
          return;
        }
        const prevList = msg.reactions[reaction] || [];
        if (prevList.includes(userId)) {
          return;
        }
        msg.reactions = {
          ...msg.reactions,
          [reaction]: [...prevList, userId],
        };
      })
      .addCase(openChatByRoomId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(openChatByRoomId.fulfilled, (state, action) => {
        state.loading = false;
        applyCreatedChatToState(state, action.payload);
      })
      .addCase(openChatByRoomId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createChat.pending, (state) => {
        state.roomCreationLoading = true;
        state.error = null;
      })
      .addCase(createChat.fulfilled, (state, action) => {
        state.roomCreationLoading = false;
        applyCreatedChatToState(state, action.payload);
      })
      .addCase(createChat.rejected, (state, action) => {
        state.roomCreationLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createChatRoom.pending, (state) => {
        state.roomCreationLoading = true;
        state.error = null;
      })
      .addCase(createChatRoom.fulfilled, (state, action) => {
        state.roomCreationLoading = false;
        applyCreatedChatToState(state, action.payload);
      })
      .addCase(createChatRoom.rejected, (state, action) => {
        state.roomCreationLoading = false;
        state.error = action.payload as string;
      })
      .addMatcher(
        (action): action is { type: string; payload: { user: User } } =>
          action.type === "user/fetchUserDetails/fulfilled",
        (state, action) => {
          const user = action.payload.user;
          state.chats.forEach((chat) => {
            if (
              chat.otherUserId === user.userId &&
              !chat.otherUserIsDeactivated
            ) {
              chat.otherUserName = user.userName;
              chat.otherUserAvatar = user.image;
            }
          });
          if (
            state.currentChat?.otherUserId === user.userId &&
            !state.currentChat.otherUserIsDeactivated
          ) {
            state.currentChat.otherUserName = user.userName;
            state.currentChat.otherUserAvatar = user.image;
          }
        }
      );
  },
});

export const {
  setCurrentChat,
  clearChatError,
  markOtherUserDeactivated,
  addMessage,
  updateChatLastMessage,
  clearMessages,
  incrementUnreadCount,
  decrementUnreadCount,
  clearUnreadForRoom,
  bumpUnreadForRoom,
  bumpRoomLastActivity,
  patchRoomPreview,
  applyMessageReactionFromEvent,
  setOnlineUserIds,
  addOnlineUserId,
  removeOnlineUserId,
  setTypingUserId,
  resetChatState,
} = chatSlice.actions;

export default chatSlice.reducer;
export const chatSelector = (state: RootState) => state.chat;
